import {
  ApolloError,
  DocumentNode,
  FetchResult,
  useApolloClient,
} from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  useGetAnswersKey,
  usePutAnswers,
} from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { PutAnswersData } from "src/generated/hooks/form-service/requests";
import { UPSERT_FORM_ANSWERS } from "src/scenes/orgAdmin/forms/graphql/mutations";
import { FormAddressSchema } from "src/schemas/Address";
import { AnswerBankMapping } from "src/services/formTemplate/answer";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { useRemoteDataMutation } from "./useRemoteDataMutation";

type GeoJsonPoint = {
  type: "Point";
  coordinates: [number, number];
};

/**
 * Structure for form_answer_options used in answers
 */
type FormAnswerOptionsInsert = {
  data: {
    form_question_option_id?: string | null;
  }[];
};

/**
 * Form answer input with only the fields actually used by upsertFormAnswers
 */
type FormAnswerInput = {
  form_id?: uuid | null;
  question_id?: uuid | null;
  free_text_answer?: string | null;
  number_answer?: number | null;
  date_answer?: string | null;
  form_answer_options?: FormAnswerOptionsInsert | null;
};

/**
 * Grades answer input with only the fields actually used
 */
type GradesAnswerInput = {
  question_id?: uuid | null;
  grade_config_id?: uuid | null;
};

/**
 * Address answer input with only the fields actually used
 */
type AddressAnswerInput = {
  form_id?: uuid | null;
  question_id?: uuid | null;
  street_address?: string | null;
  street_address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  geom?: GeoJsonPoint;
};

/**
 * Nested form answer data for custom question answers
 */
type NestedFormAnswerData = {
  question_id?: uuid | null;
  free_text_answer?: string | null;
  number_answer?: number | null;
  date_answer?: string | null;
  form_answer_options?: FormAnswerOptionsInsert | null;
};

/**
 * Custom question answer input with only the fields actually used
 */
type CustomQuestionAnswerInput = {
  question_id?: uuid | null;
  form_answer?: {
    data: NestedFormAnswerData;
  } | null;
};

/**
 * Base input variables required for all upsertFormAnswers calls
 */
export type UpsertFormAnswersInput = {
  /** The form ID to upsert answers for */
  form_id: uuid;
  /** Standard form answers (free text, number, date, single/multi select) */
  answers: FormAnswerInput[];
  /** Grade selection answers */
  grades_answers: GradesAnswerInput[];
  /** Address answers */
  address_answers: AddressAnswerInput[];
  /** Custom question type answers */
  custom_question_answers: CustomQuestionAnswerInput[];
  /** Question IDs for single/multi select answers (used to delete existing options) */
  single_or_multi_select_question_ids: uuid[];
  /** Boolean expressions for address answers to delete */
  address_answer_deletes: GQL.form_address_bool_exp[];
  /** Custom question IDs being updated */
  custom_question_ids: uuid[];
  /** Answer bank IDs for custom question answers */
  answer_bank_ids: uuid[];
  /** Person answer bank answers */
  answer_bank_answers: GQL.person_answer_insert_input[];
  /** New answer bank entries to create */
  new_answer_bank_entries: GQL.person_answer_bank_insert_input[];
  /** Custom question IDs whose answers should be deleted (cleared CQTs) */
  deleted_answers_custom_question_ids: uuid[];
};

/*
 * This hook is used to upsert form answers.
 * It uses the form-service API to upsert form answers when enabled.
 * Otherwise, it uses the GraphQL API (UPSERT_FORM_ANSWERS_AND_DELETE_SCHOOLS_RANK).
 * School ranking fields are optional and will be filled with empty values if not provided.
 */
export function useFormAnswerUpsert() {
  const flags = useFlags(["form-service"]);
  const isFormServiceEnabled = flags["form-service"].enabled;
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();

  const [upsertFormAnswersGQL, upsertFormAnswersGQLStatus] =
    useRemoteDataMutation<GQL.UpsertFormAnswer, GQL.UpsertFormAnswerVariables>(
      UPSERT_FORM_ANSWERS
    );

  const {
    mutateAsync: upsertFormAnswersREST,
    isPending: isUpsertFormAnswersRESTPending,
  } = usePutAnswers(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const upsertFormAnswers = async (
    inputVars: UpsertFormAnswersInput,
    additionalVariables?: {
      answerBankMappingIds: AnswerBankMapping[];
      externalRelationships?: GQL.person_answer_bank_external_relationship_insert_input[];
      externalRelationshipsToDelete?: uuid[];
    },
    options?: {
      refetchQueries?: DocumentNode[];
      awaitRefetchQueries?: boolean;
    }
  ): Promise<FetchResult<GQL.UpsertFormAnswer>> => {
    // Add default empty school ranking fields if not provided
    const variables: GQL.UpsertFormAnswerVariables = {
      ...inputVars,
      external_relationships: additionalVariables?.externalRelationships ?? [],
      external_relationship_deletes:
        additionalVariables?.externalRelationshipsToDelete ?? [],
    };

    if (!isFormServiceEnabled) {
      const result = await upsertFormAnswersGQL({
        variables,
        refetchQueries: options?.refetchQueries,
        awaitRefetchQueries: options?.awaitRefetchQueries,
      });
      try {
        await FSR.createRuleExecutions({
          body: {
            data: {
              targets: [{ type: "form", formId: variables.form_id }],
            },
          },
        });
      } catch (error) {
        console.warn("Error executing form rules:", error);
      }
      return result;
    }

    // Convert GraphQL variables to REST format
    const restPayload: PutAnswersData = {
      path: {
        formId: variables.form_id,
      },
      body: {
        data: {},
      },
    };

    // Process standard answers
    variables.answers.forEach((answer) => {
      if (!answer.question_id) return;

      // Use !== undefined so explicit null values clear answers via form-service
      if (answer.date_answer !== undefined) {
        restPayload.body.data[answer.question_id] = answer.date_answer;
      } else if (answer.number_answer !== undefined) {
        restPayload.body.data[answer.question_id] = answer.number_answer;
      } else if (answer.free_text_answer !== undefined) {
        restPayload.body.data[answer.question_id] = answer.free_text_answer;
      } else if (Array.isArray(answer.form_answer_options?.data)) {
        // Handle single/multi select answers
        const optionIds = answer.form_answer_options?.data
          .map((opt) => opt.form_question_option_id)
          .filter((id): id is string => id != null);
        restPayload.body.data[answer.question_id] = optionIds ?? [];
      }
    });

    // Process grade answers
    variables.grades_answers.forEach((answer) => {
      if (answer.question_id && answer.grade_config_id !== undefined) {
        restPayload.body.data[answer.question_id] = answer.grade_config_id;
      }
    });

    // Process address answers
    variables.address_answers.forEach((answer) => {
      if (answer.question_id) {
        const address = FormAddressSchema.parse(answer);
        restPayload.body.data[answer.question_id] = address;
      }
    });

    // Process custom question answers
    // Set nested question answers
    const parentQuestionIds = new Set<string>();
    variables.custom_question_answers.forEach((answer) => {
      if (answer.question_id) {
        const parentQuestionId = answer.question_id;
        parentQuestionIds.add(parentQuestionId);

        const answersByQuestionId: {
          [key: string]: string | Array<string> | number | null;
        } = {};
        const customQuestionAnswer = answer.form_answer;
        if (!customQuestionAnswer?.data.question_id) return;
        const nestedQuestionId = customQuestionAnswer.data.question_id;

        if (customQuestionAnswer?.data.free_text_answer) {
          answersByQuestionId[nestedQuestionId] =
            customQuestionAnswer?.data.free_text_answer;
        } else if (customQuestionAnswer?.data.number_answer) {
          answersByQuestionId[nestedQuestionId] =
            customQuestionAnswer?.data.number_answer;
        } else if (customQuestionAnswer?.data.date_answer) {
          answersByQuestionId[nestedQuestionId] =
            customQuestionAnswer?.data.date_answer;
        } else if (
          customQuestionAnswer?.data.form_answer_options?.data.length
        ) {
          answersByQuestionId[nestedQuestionId] =
            customQuestionAnswer?.data.form_answer_options?.data
              .map((opt) => opt.form_question_option_id)
              .filter((id): id is string => id != null);
        } else {
          answersByQuestionId[nestedQuestionId] = null;
        }

        if (restPayload.body.data[parentQuestionId]) {
          const existingData = restPayload.body.data[parentQuestionId] as {
            answersByQuestionId: Record<string, any>;
          };
          existingData.answersByQuestionId = {
            ...(existingData.answersByQuestionId ?? {}),
            ...answersByQuestionId,
          };
        } else {
          restPayload.body.data[parentQuestionId] = {
            answersByQuestionId,
          };
        }
      }
    });

    // Set answerBankId and referenceId for each parent question
    for (const parentQuestionId of parentQuestionIds) {
      const answerBankMapping = additionalVariables?.answerBankMappingIds.find(
        (ab) => ab.questionId === parentQuestionId
      );
      const answerBankId = answerBankMapping?.answerBankId;

      let referenceId: string | null | undefined;

      if (!answerBankId) {
        // New entry: check newAnswerBankEntries
        const newEntry = variables.new_answer_bank_entries.find(
          (entry) =>
            entry.custom_question_answer_bank_relationships?.data?.[0]
              ?.custom_question_id === parentQuestionId
        );
        referenceId =
          newEntry?.person_answer_bank_external_relationship?.data?.external_id;
      } else if (
        // Existing entry being cleared
        additionalVariables?.externalRelationshipsToDelete?.includes(
          answerBankId
        )
      ) {
        referenceId = null;
      } else {
        // Update existing entry
        referenceId = answerBankMapping?.referenceId;
      }

      const cqtAnswerData = restPayload.body.data[parentQuestionId] as {
        answersByQuestionId: Record<string, any>;
        answerBankId?: string;
        referenceId?: string | null;
      };
      cqtAnswerData.answerBankId = answerBankId;
      cqtAnswerData.referenceId = referenceId;
    }

    // handle clearing answers of CQTs
    if (variables.deleted_answers_custom_question_ids?.length) {
      for (const questionId of variables.deleted_answers_custom_question_ids) {
        restPayload.body.data[questionId] = null;
      }
    }
    if (Object.keys(restPayload.body.data).length === 0) {
      return {
        data: {} as GQL.UpsertFormAnswer,
      };
    }

    await upsertFormAnswersREST(restPayload);
    await queryClient.invalidateQueries({
      queryKey: [useGetAnswersKey],
    });
    await apolloClient.refetchQueries({
      include: options?.refetchQueries,
    });

    // Return a mock FetchResult to maintain interface compatibility
    return {
      data: {} as GQL.UpsertFormAnswer,
    };
  };

  const upsertFormAnswersStatus = React.useMemo(() => {
    if (isFormServiceEnabled) {
      return {
        remoteData: isUpsertFormAnswersRESTPending
          ? RD.loading<ApolloError, GQL.UpsertFormAnswer>(undefined)
          : RD.notAsked<ApolloError, GQL.UpsertFormAnswer>(),
      };
    }
    return upsertFormAnswersGQLStatus;
  }, [
    isFormServiceEnabled,
    isUpsertFormAnswersRESTPending,
    upsertFormAnswersGQLStatus,
  ]);

  return {
    upsertFormAnswers,
    upsertFormAnswersStatus,
  };
}
